import { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Plus, Save, Ban, CheckCircle2, Package, Users as UsersIcon, X, Search } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;

// Normalize the various shapes a list endpoint might return
// (plain array, { data: [...] }, { plans: [...] }, { users: [...] }, etc.)
const _extractList = (resp, ...keys) => {
  if (Array.isArray(resp)) return resp;
  if (!resp || typeof resp !== 'object') return [];
  if (Array.isArray(resp.data)) return resp.data;
  for (const k of keys) {
    if (Array.isArray(resp[k])) return resp[k];
  }
  return [];
};

const STATUS_LABELS = {
  active:          { label: 'Aktiv',                color: 'bg-green-100 text-green-700' },
  pending_payment: { label: 'Zahlung ausstehend',   color: 'bg-yellow-100 text-yellow-700' },
  payment_failed:  { label: 'Zahlung fehlgeschlagen', color: 'bg-red-100 text-red-700' },
  cancelling:      { label: 'Wird gekündigt',       color: 'bg-orange-100 text-orange-700' },
  cancelled:       { label: 'Gekündigt',            color: 'bg-gray-100 text-gray-500' },
  free:            { label: 'Kein Plan',            color: 'bg-gray-100 text-gray-500' },
};

const statusInfo = (s) => STATUS_LABELS[s] || STATUS_LABELS.free;

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return iso; }
};

const capitalize = (s) => (s ? String(s).charAt(0).toUpperCase() + String(s).slice(1) : s);

// ─────────────────────────── Featured Badge ────────────────────────────
const FeatureBadge = ({ enabled, label }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${enabled ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-gray-100 text-gray-500 border-gray-200'}`}>
    {label}
  </span>
);

// ─────────────────────────── Custom Plan Form ──────────────────────────
const CustomPlanForm = ({ getAuthHeader, onCreated, onCancel }) => {
  const [name, setName] = useState('');
  const [stripePriceId, setStripePriceId] = useState('');
  const [priceEuros, setPriceEuros] = useState('');
  const [creditsMonthly, setCreditsMonthly] = useState('');
  const [projects, setProjects] = useState('1');
  const [channelsSocial, setChannelsSocial] = useState('1');
  const [channelsWebsite, setChannelsWebsite] = useState('1');
  const [videosMonthly, setVideosMonthly] = useState('0');
  const [videosAddonMonthly, setVideosAddonMonthly] = useState('0');
  const [videoGen, setVideoGen] = useState(false);
  const [pressDist, setPressDist] = useState(false);
  const [prioSupport, setPrioSupport] = useState(false);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!name.trim()) { toast.error('Name ist erforderlich'); return; }
    const payload = {
      name: name.trim(),
      stripe_price_id: stripePriceId.trim(),
      price_euros: parseFloat(priceEuros) || 0,
      limits: {
        credits_monthly: parseInt(creditsMonthly, 10) || 0,
        projects: parseInt(projects, 10) || 0,
        connected_channels_social: parseInt(channelsSocial, 10) || 0,
        connected_channels_website: parseInt(channelsWebsite, 10) || 0,
        videos_monthly: parseInt(videosMonthly, 10) || 0,
        videos_addon_monthly: parseInt(videosAddonMonthly, 10) || 0,
      },
      features: {
        video_generation: videoGen,
        press_release_distribution: pressDist,
        priority_support: prioSupport,
      },
    };
    setSaving(true);
    try {
      await axios.post(`${API}/admin/plans/create-custom`, payload, { headers: getAuthHeader() });
      toast.success('Custom Paket erstellt');
      onCreated && onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erstellung fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm" data-testid="custom-plan-form">
      <div className="flex items-center justify-between mb-4">
        <h4 className="text-lg font-semibold text-gray-900">Neues Custom Paket</h4>
        {onCancel && <button onClick={onCancel} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-gray-600 block mb-1">Name</label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Agency Pro" className="text-gray-900 placeholder:text-gray-400" data-testid="cp-name" />
        </div>
        <div>
          <label className="text-xs text-gray-600 block mb-1">Stripe Price ID</label>
          <Input value={stripePriceId} onChange={(e) => setStripePriceId(e.target.value)} placeholder="price_..." className="text-gray-900 placeholder:text-gray-400" data-testid="cp-stripe-price-id" />
        </div>
        <div>
          <label className="text-xs text-gray-600 block mb-1">Preis (€)</label>
          <Input type="number" step="0.01" value={priceEuros} onChange={(e) => setPriceEuros(e.target.value)} placeholder="299" className="text-gray-900 placeholder:text-gray-400" data-testid="cp-price" />
        </div>
        <div>
          <label className="text-xs text-gray-600 block mb-1">Credits / Monat</label>
          <Input type="number" value={creditsMonthly} onChange={(e) => setCreditsMonthly(e.target.value)} placeholder="25000" className="text-gray-900 placeholder:text-gray-400" data-testid="cp-credits" />
        </div>
        <div>
          <label className="text-xs text-gray-600 block mb-1">Projekte</label>
          <Input type="number" value={projects} onChange={(e) => setProjects(e.target.value)} className="text-gray-900 placeholder:text-gray-400" data-testid="cp-projects" />
        </div>
        <div>
          <label className="text-xs text-gray-600 block mb-1">Social Media Channels</label>
          <Input type="number" value={channelsSocial} onChange={(e) => setChannelsSocial(e.target.value)} className="text-gray-900 placeholder:text-gray-400" data-testid="cp-channels-social" />
        </div>
        <div>
          <label className="text-xs text-gray-600 block mb-1">Webseiten</label>
          <Input type="number" value={channelsWebsite} onChange={(e) => setChannelsWebsite(e.target.value)} className="text-gray-900 placeholder:text-gray-400" data-testid="cp-channels-web" />
        </div>
        <div>
          <label className="text-xs text-gray-600 block mb-1">Videos / Monat</label>
          <Input type="number" value={videosMonthly} onChange={(e) => setVideosMonthly(e.target.value)} className="text-gray-900 placeholder:text-gray-400" data-testid="cp-videos" />
        </div>
        <div>
          <label className="text-xs text-gray-600 block mb-1">Add-on Videos / Monat</label>
          <Input type="number" min={0} value={videosAddonMonthly} onChange={(e) => setVideosAddonMonthly(e.target.value)} className="text-gray-900 placeholder:text-gray-400" data-testid="cp-videos-addon" />
        </div>
      </div>
      <div className="mt-4 flex flex-wrap gap-4">
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={videoGen} onChange={(e) => setVideoGen(e.target.checked)} data-testid="cp-toggle-video" />
          <span className="text-sm text-gray-700">Videogenerierung</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={pressDist} onChange={(e) => setPressDist(e.target.checked)} data-testid="cp-toggle-press" />
          <span className="text-sm text-gray-700">Pressemitteilung-Verteilung</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={prioSupport} onChange={(e) => setPrioSupport(e.target.checked)} data-testid="cp-toggle-priority" />
          <span className="text-sm text-gray-700">Priority Support</span>
        </label>
      </div>
      <div className="mt-5 flex gap-2">
        <Button onClick={submit} disabled={saving}
                className="bg-[#1e9ead] hover:bg-[#197b87] text-white" data-testid="cp-submit">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
          Paket anlegen
        </Button>
      </div>
    </div>
  );
};

// ─────────────────────────── Main Component ─────────────────────────────
export default function AdminBillingSection({ getAuthHeader }) {
  const [tab, setTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [editingCredits, setEditingCredits] = useState(null);
  const [creditsInput, setCreditsInput] = useState('');
  const [confirmCancel, setConfirmCancel] = useState(null);
  const [assignFor, setAssignFor] = useState(null); // user being assigned a plan
  const [assignPlanId, setAssignPlanId] = useState('');
  const [busy, setBusy] = useState(null);
  const [showCreatePlan, setShowCreatePlan] = useState(false);
  // Search filter for the assign-plan modal
  const [planFilter, setPlanFilter] = useState('');
  // Per-row reset-day input drafts (keyed by user id)
  const [resetDayDrafts, setResetDayDrafts] = useState({});
  // Assign-plan modal extra fields
  const [assignMode, setAssignMode] = useState('immediate'); // 'immediate' | 'payment'
  const [assignResetDay, setAssignResetDay] = useState('1');
  const [assignValidFrom, setAssignValidFrom] = useState('');
  const [assignPaymentDeadline, setAssignPaymentDeadline] = useState('');

  const filteredPlans = useMemo(() => {
    const q = planFilter.trim().toLowerCase();
    if (!q) return plans;
    return plans.filter(p => (p.name || '').toLowerCase().includes(q) || (p.type || '').toLowerCase().includes(q));
  }, [plans, planFilter]);

  const loadUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const r = await axios.get(`${API}/admin/users-with-plans`, { headers: getAuthHeader() });
      setUsers(_extractList(r.data, 'users', 'items'));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Nutzer konnten nicht geladen werden');
    } finally { setLoadingUsers(false); }
  }, [getAuthHeader]);

  const loadPlans = useCallback(async () => {
    setLoadingPlans(true);
    try {
      const r = await axios.get(`${API}/admin/plans`, { headers: getAuthHeader() });
      setPlans(_extractList(r.data, 'plans', 'items'));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Pakete konnten nicht geladen werden');
    } finally { setLoadingPlans(false); }
  }, [getAuthHeader]);

  useEffect(() => { loadUsers(); loadPlans(); }, [loadUsers, loadPlans]);

  const handleAdjustCredits = async (user_id) => {
    const n = parseInt(creditsInput, 10);
    if (!Number.isFinite(n) || n < 0) { toast.error('Bitte eine gültige Zahl eingeben'); return; }
    setBusy(`credits-${user_id}`);
    try {
      await axios.post(`${API}/admin/billing/adjust-credits`, { user_id, credits: n }, { headers: getAuthHeader() });
      toast.success('Credits aktualisiert');
      setEditingCredits(null); setCreditsInput('');
      await loadUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Update fehlgeschlagen'); }
    finally { setBusy(null); }
  };

  const handleCancel = async (user_id) => {
    setBusy(`cancel-${user_id}`);
    try {
      await axios.post(`${API}/admin/billing/cancel-subscription`, { user_id }, { headers: getAuthHeader() });
      toast.success('Subscription wird zum Periodenende gekündigt');
      setConfirmCancel(null);
      await loadUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Kündigung fehlgeschlagen'); }
    finally { setBusy(null); }
  };

  const handleAssign = async () => {
    if (!assignFor || !assignPlanId) { toast.error('Bitte einen Plan auswählen'); return; }
    const payload = {
      user_id: assignFor.id,
      plan_id: assignPlanId,
      reset_day: parseInt(assignResetDay, 10) || 1,
      payment_required: assignMode === 'payment',
    };
    if (assignValidFrom) payload.valid_from = assignValidFrom;
    if (assignMode === 'payment') {
      if (!assignPaymentDeadline) { toast.error('Bitte Zahlungsdeadline angeben'); return; }
      payload.payment_deadline = assignPaymentDeadline;
    }
    setBusy('assign');
    try {
      const r = await axios.post(`${API}/admin/plans/assign-to-user`, payload, { headers: getAuthHeader() });
      if (assignMode === 'payment' && r.data?.invoice_url) {
        toast.success('Plan zugewiesen + Rechnung gesendet ✓', {
          description: r.data.invoice_url,
          action: { label: 'Rechnung öffnen', onClick: () => window.open(r.data.invoice_url, '_blank') },
        });
      } else if (assignMode === 'payment' && r.data?.stripe_error) {
        toast.warning('Plan zugewiesen, Stripe-Rechnung schlug fehl', { description: String(r.data.stripe_error).slice(0, 200) });
      } else {
        toast.success('Plan zugewiesen ✓');
      }
      setAssignFor(null); setAssignPlanId(''); setAssignValidFrom(''); setAssignPaymentDeadline(''); setAssignMode('immediate'); setAssignResetDay('1');
      await loadUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Zuweisung fehlgeschlagen');
    } finally { setBusy(null); }
  };

  const handleSetResetDay = async (user_id) => {
    const raw = resetDayDrafts[user_id];
    const day = parseInt(raw, 10);
    if (!Number.isFinite(day) || day < 1 || day > 28) {
      toast.error('Reset-Tag muss zwischen 1 und 28 liegen');
      return;
    }
    setBusy(`reset-${user_id}`);
    try {
      const r = await axios.post(`${API}/admin/plans/set-reset-date`,
        { user_id, reset_day: day },
        { headers: getAuthHeader() });
      const nextIso = r.data?.next_reset;
      toast.success(`Reset-Tag gesetzt (nächster Reset: ${formatDate(nextIso)})`);
      await loadUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reset-Tag konnte nicht gesetzt werden');
    } finally { setBusy(null); }
  };

  return (
    <div className="space-y-4" data-testid="admin-billing-section">
      <div className="flex gap-1 bg-[#0A1628] rounded-lg p-1 w-fit">
        <button onClick={() => setTab('users')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${tab === 'users' ? 'bg-[#1A2B42] text-white' : 'text-[#5A6C7D] hover:text-white'}`}
                data-testid="admin-billing-tab-users">
          <UsersIcon className="w-3.5 h-3.5 inline mr-1.5" />Alle Nutzer &amp; Pläne
        </button>
        <button onClick={() => setTab('plans')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${tab === 'plans' ? 'bg-[#1A2B42] text-white' : 'text-[#5A6C7D] hover:text-white'}`}
                data-testid="admin-billing-tab-plans">
          <Package className="w-3.5 h-3.5 inline mr-1.5" />Pakete
        </button>
      </div>

      {/* ─────── USERS TAB ─────── */}
      {tab === 'users' && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          {loadingUsers ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#1e9ead]" /></div>
          ) : users.length === 0 ? (
            <div className="px-6 py-12 text-center text-sm text-gray-500">Keine Nutzer vorhanden</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium">Name / Email</th>
                    <th className="px-4 py-3 text-left font-medium">Plan</th>
                    <th className="px-4 py-3 text-left font-medium">Status</th>
                    <th className="px-4 py-3 text-left font-medium">Credits</th>
                    <th className="px-4 py-3 text-left font-medium">Videos</th>
                    <th className="px-4 py-3 text-left font-medium">Reset-Tag</th>
                    <th className="px-4 py-3 text-left font-medium">Verlängerung</th>
                    <th className="px-4 py-3 text-right font-medium">Aktionen</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {users.map(u => {
                    const usage = u.usage_current_month || {};
                    const planObj = plans.find(p => p._id === u.plan_id);
                    const creditsTotal = planObj?.limits?.credits_monthly ?? u.credits_total ?? 0;
                    const creditsUsed = usage.credits_used ?? 0;
                    const videosMonthly = planObj?.limits?.videos_monthly ?? 0;
                    const videosAddonMonthly = planObj?.limits?.videos_addon_monthly ?? 0;
                    const videosUsed = usage.videos_used ?? 0;
                    const videosAddonUsed = usage.videos_addon_used ?? 0;
                    const videosTotalAll = videosMonthly + videosAddonMonthly;
                    const videosUsedAll = videosUsed + videosAddonUsed;
                    const resetDay = usage.reset_day ?? 1;
                    const resetDayDraft = resetDayDrafts[u.id] ?? String(resetDay);
                    return (
                      <tr key={u.id || u._id} className="hover:bg-gray-50" data-testid={`admin-user-row-${u.id}`}>
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900">{u.name || '—'}</div>
                          <div className="text-xs text-gray-500">{u.email}</div>
                        </td>
                        <td className="px-4 py-3 text-gray-900">{u.plan_name || capitalize(u.subscription_plan) || '—'}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusInfo(u.subscription_status).color}`}>
                            {statusInfo(u.subscription_status).label}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-900">
                          {editingCredits === u.id ? (
                            <div className="flex items-center gap-1">
                              <Input type="number" value={creditsInput} onChange={(e) => setCreditsInput(e.target.value)}
                                     className="w-24 h-7 text-sm text-gray-900" data-testid={`credits-input-${u.id}`} />
                              <Button size="sm" onClick={() => handleAdjustCredits(u.id)} disabled={busy === `credits-${u.id}`}
                                      className="h-7 px-2 bg-[#1e9ead] hover:bg-[#197b87] text-white"
                                      data-testid={`credits-save-${u.id}`}>
                                {busy === `credits-${u.id}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                              </Button>
                              <button onClick={() => { setEditingCredits(null); setCreditsInput(''); }}
                                      className="text-xs text-gray-500 hover:text-gray-700">×</button>
                            </div>
                          ) : (
                            <span>{Number(creditsUsed).toLocaleString('de-DE')} / {Number(creditsTotal).toLocaleString('de-DE')}</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-gray-700" data-testid={`videos-cell-${u.id}`}>
                          <div className="text-gray-900">
                            {Number(videosUsedAll).toLocaleString('de-DE')} / {Number(videosTotalAll).toLocaleString('de-DE')}
                          </div>
                          <div className="text-[11px] text-gray-500">
                            Paket: {videosUsed}/{videosMonthly} | Add-on: {videosAddonUsed}/{videosAddonMonthly}
                          </div>
                        </td>
                        <td className="px-4 py-3" data-testid={`reset-day-cell-${u.id}`}>
                          <div className="flex items-center gap-1">
                            <Input
                              type="number" min={1} max={28}
                              value={resetDayDraft}
                              onChange={(e) => setResetDayDrafts(prev => ({ ...prev, [u.id]: e.target.value }))}
                              className="w-14 h-7 text-sm text-gray-900"
                              data-testid={`reset-day-input-${u.id}`}
                            />
                            <Button size="sm" onClick={() => handleSetResetDay(u.id)}
                                    disabled={busy === `reset-${u.id}`}
                                    className="h-7 px-2 bg-[#1e9ead] hover:bg-[#197b87] text-white"
                                    data-testid={`reset-day-save-${u.id}`}>
                              {busy === `reset-${u.id}` ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Setzen'}
                            </Button>
                          </div>
                          <div className="text-[11px] text-gray-500 mt-1 whitespace-nowrap">
                            Nächster Reset: {formatDate(usage.reset_date)}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{formatDate(u.subscription_renewal_date)}</td>
                        <td className="px-4 py-3 text-right space-x-1 whitespace-nowrap">
                          <Button size="sm" variant="outline"
                                  onClick={() => {
                                    setAssignFor(u);
                                    setAssignPlanId(u.plan_id || '');
                                    // Defaults: reset_day=user's current or 1, valid_from=today, mode=immediate
                                    const _today = new Date().toISOString().slice(0, 10);
                                    setAssignResetDay(String(u?.usage_current_month?.reset_day || 1));
                                    setAssignValidFrom(_today);
                                    setAssignPaymentDeadline('');
                                    setAssignMode('immediate');
                                  }}
                                  data-testid={`assign-plan-btn-${u.id}`}>
                            Plan zuweisen
                          </Button>
                          <Button size="sm" variant="outline"
                                  onClick={() => { setEditingCredits(u.id); setCreditsInput(String(u.credits_remaining || creditsUsed || 0)); }}
                                  disabled={editingCredits === u.id}
                                  data-testid={`edit-credits-btn-${u.id}`}>
                            Credits anpassen
                          </Button>
                          {confirmCancel === u.id ? (
                            <span className="inline-flex items-center gap-1 ml-1">
                              <span className="text-xs text-red-600">Sicher?</span>
                              <Button size="sm" onClick={() => handleCancel(u.id)} disabled={busy === `cancel-${u.id}`}
                                      className="h-7 px-2 bg-red-600 hover:bg-red-700 text-white"
                                      data-testid={`cancel-confirm-${u.id}`}>
                                {busy === `cancel-${u.id}` ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Ja'}
                              </Button>
                              <button onClick={() => setConfirmCancel(null)} className="text-xs text-gray-500 hover:text-gray-700">Nein</button>
                            </span>
                          ) : (
                            <Button size="sm" variant="outline"
                                    onClick={() => setConfirmCancel(u.id)}
                                    disabled={!u.stripe_subscription_id}
                                    data-testid={`cancel-btn-${u.id}`}>
                              <Ban className="w-3 h-3 mr-1" />Kündigen
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ─────── PLANS TAB ─────── */}
      {tab === 'plans' && (
        <div className="space-y-4">
          {!showCreatePlan && (
            <Button onClick={() => setShowCreatePlan(true)}
                    className="bg-[#1e9ead] hover:bg-[#197b87] text-white" data-testid="show-create-plan-btn">
              <Plus className="w-4 h-4 mr-2" />Neues Custom Paket
            </Button>
          )}
          {showCreatePlan && (
            <CustomPlanForm
              getAuthHeader={getAuthHeader}
              onCreated={async () => { setShowCreatePlan(false); await loadPlans(); }}
              onCancel={() => setShowCreatePlan(false)}
            />
          )}

          {loadingPlans ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#1e9ead]" /></div>
          ) : plans.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 px-6 py-12 text-center text-sm text-gray-500">
              Keine Pakete vorhanden
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {plans.map(p => (
                <div key={p._id} className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm" data-testid={`plan-card-${p._id}`}>
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="text-lg font-semibold text-gray-900">{p.name}</h4>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${p.type === 'custom' ? 'bg-purple-100 text-purple-700' : 'bg-sky-100 text-sky-700'}`}>
                      {p.type === 'custom' ? 'Custom' : 'Standard'}
                    </span>
                  </div>
                  <div className="text-3xl font-bold text-[#1e9ead] mb-3">€{Number(p.price_euros).toLocaleString('de-DE')}<span className="text-sm text-gray-500 font-normal"> / Monat</span></div>
                  <ul className="space-y-1.5 text-sm text-gray-700 mb-4">
                    <li><span className="text-gray-500">Credits:</span> <strong>{Number(p.limits?.credits_monthly || 0).toLocaleString('de-DE')}</strong> / Monat</li>
                    <li><span className="text-gray-500">Videos im Paket:</span> <strong>{Number(p.limits?.videos_monthly || 0)}</strong> <span className="text-xs text-gray-400">(Credit-basiert)</span></li>
                    {Number(p.limits?.videos_addon_monthly || 0) > 0 && (
                      <li>
                        <span className="text-gray-500">Add-on Videos:</span>{' '}
                        <strong className="text-[#1D9E75]">{Number(p.limits?.videos_addon_monthly || 0)}</strong>{' '}
                        <span className="text-xs text-gray-400">(Credit-unabhängig)</span>
                      </li>
                    )}
                    <li><span className="text-gray-500">Social Channels:</span> <strong>{p.limits?.connected_channels_social || 0}</strong></li>
                    <li><span className="text-gray-500">Webseiten:</span> <strong>{p.limits?.connected_channels_website || 0}</strong></li>
                    <li><span className="text-gray-500">Projekte:</span> <strong>{p.limits?.projects || 0}</strong></li>
                  </ul>
                  <div className="flex flex-wrap gap-1.5">
                    <FeatureBadge enabled={!!p.features?.video_generation} label="Video" />
                    <FeatureBadge enabled={!!p.features?.press_release_distribution} label="PR-Verteilung" />
                    <FeatureBadge enabled={!!p.features?.priority_support} label="Priority Support" />
                  </div>
                  {p.stripe_price_id && (
                    <p className="text-[10px] text-gray-400 font-mono mt-3 truncate" title={p.stripe_price_id}>{p.stripe_price_id}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ─────── Assign Plan Modal ─────── */}
      {assignFor && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setAssignFor(null)}>
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}
               data-testid="assign-plan-modal">
            <h4 className="text-lg font-semibold text-gray-900 mb-1">Plan zuweisen</h4>
            <p className="text-sm text-gray-500 mb-4">An <strong>{assignFor.name || assignFor.email}</strong></p>

            {/* Search filter */}
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              <Input
                value={planFilter}
                onChange={(e) => setPlanFilter(e.target.value)}
                placeholder="Plan filtern (Name oder Typ)…"
                className="pl-9 text-sm text-gray-900 placeholder:text-gray-400"
                data-testid="assign-plan-filter"
              />
            </div>

            {/* Custom click-list (replaces native <select> for cross-device reliability) */}
            <div
              className="border border-gray-300 rounded-md max-h-64 overflow-y-auto mb-2 bg-white"
              data-testid="assign-plan-list"
            >
              {filteredPlans.length === 0 ? (
                <div className="px-3 py-4 text-sm text-gray-400 text-center">Keine Pläne gefunden</div>
              ) : (
                filteredPlans.map(p => {
                  const selected = assignPlanId === p._id;
                  return (
                    <button
                      key={p._id}
                      type="button"
                      onClick={() => setAssignPlanId(p._id)}
                      className={`w-full text-left px-3 py-2.5 text-sm flex items-center justify-between gap-2 border-b border-gray-100 last:border-b-0 transition-colors ${selected ? 'bg-[#1e9ead]/10 text-[#0a1628] font-medium' : 'text-gray-900 hover:bg-gray-50'}`}
                      data-testid={`assign-plan-option-${p._id}`}
                    >
                      <span className="truncate">
                        {p.name}{' '}
                        <span className="text-xs text-gray-500">
                          ({p.type === 'custom' ? 'Custom' : 'Standard'}) — €{p.price_euros} / Monat
                        </span>
                      </span>
                      {selected && <CheckCircle2 className="w-4 h-4 text-[#1e9ead] flex-shrink-0" />}
                    </button>
                  );
                })
              )}
            </div>
            <p className="text-xs text-gray-500 mb-4">
              {filteredPlans.length} von {plans.length} Plänen
            </p>

            {/* Reset-Tag + Gültig ab */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Reset-Tag (1–28)</label>
                <Input
                  type="number"
                  min={1}
                  max={28}
                  value={assignResetDay}
                  onChange={(e) => setAssignResetDay(e.target.value)}
                  className="text-sm text-gray-900"
                  data-testid="assign-plan-reset-day"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Gültig ab</label>
                <Input
                  type="date"
                  value={assignValidFrom}
                  onChange={(e) => setAssignValidFrom(e.target.value)}
                  className="text-sm text-gray-900"
                  data-testid="assign-plan-valid-from"
                />
              </div>
            </div>

            {/* Mode toggle */}
            <div className="mb-3">
              <label className="block text-xs font-medium text-gray-700 mb-2">Modus</label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setAssignMode('immediate')}
                  className={`px-3 py-2 text-sm rounded-md border transition-colors ${assignMode === 'immediate' ? 'bg-[#1e9ead] text-white border-[#1e9ead]' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                  data-testid="assign-plan-mode-immediate"
                >
                  Ohne Zahlungsaufforderung
                </button>
                <button
                  type="button"
                  onClick={() => setAssignMode('payment')}
                  className={`px-3 py-2 text-sm rounded-md border transition-colors ${assignMode === 'payment' ? 'bg-[#1e9ead] text-white border-[#1e9ead]' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                  data-testid="assign-plan-mode-payment"
                >
                  Mit Zahlungsaufforderung
                </button>
              </div>
            </div>

            {/* Conditional payment deadline */}
            {assignMode === 'payment' && (
              <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-md">
                <label className="block text-xs font-medium text-gray-700 mb-1">Zahlungsdeadline</label>
                <Input
                  type="date"
                  min={(() => { const d = new Date(); d.setDate(d.getDate() + 1); return d.toISOString().slice(0, 10); })()}
                  value={assignPaymentDeadline}
                  onChange={(e) => setAssignPaymentDeadline(e.target.value)}
                  className="text-sm text-gray-900 bg-white"
                  data-testid="assign-plan-payment-deadline"
                />
                <p className="text-xs text-gray-600 mt-2">
                  Der User erhält eine Stripe-Rechnung per Email. Er kann die App bis zur Deadline nutzen.
                </p>
              </div>
            )}

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => { setAssignFor(null); setPlanFilter(''); }}>Abbrechen</Button>
              <Button onClick={handleAssign} disabled={busy === 'assign' || !assignPlanId}
                      className="bg-[#1e9ead] hover:bg-[#197b87] text-white"
                      data-testid="assign-plan-confirm-btn">
                {busy === 'assign' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                Zuweisen
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
